// frontend/src/services/upsDriverService.js
// Responsibility: Talk to the backend and return driver-shaped objects. Centralize all UPS API calls + normalization.
// Refactored Phase 8.7B: "Reads" for Active/Reserved now belong to assignmentService.
// This service now handles:
// 1. Fetching Banned drivers (global list).
// 2. All "Write" actions (Reserve, Activate, Ban, Unban).
// 3. Phase 9.1 internal-driver lifecycle for company-owned drivers.
import axios from 'axios';

/* ---------- helpers ---------- */
const pick = (...vals) => {
  for (const v of vals) if (v !== undefined && v !== null) return v;
  return undefined;
};
const toNum = v => {
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
};

const asArray = (rows) => (Array.isArray(rows) ? rows : (rows?.rows || []));

const normalizeSourceParam = (source) => {
  const value = String(source || 'all').toLowerCase();
  if (value === 'agency') return 'agency';
  if (value === 'internal') return 'internal';
  return undefined;
};

/** Ensure Active/Reserved rows carry a `status` field and expected props */
// Returned `status` is compatibility/display metadata only and must not be used as lane truth.
// Kept because actions like 'activateDriver' still return a single driver row that needs this normalization.
const tagLegacyStatusForCompatibility = (rows, status) =>
  normalizeDrivers(rows).map((d) => ({
    ...d,
    status,
  }));

/**
 * Convert canonical driver DTOs → driver-shaped entities
 */
const normalizeDrivers = (rows) =>
  asArray(rows).map(d => {
    const id = toNum(pick(d.id, d.driver_id, d.driverId, d.driver?.id));
    const agency_id = toNum(pick(d.agency_id, d.agencyId, d.agency?.id));
    const company_id = toNum(pick(d.company_id, d.companyId, d.company?.id));

        return {
      ...d,
      id,
      driver_id: id,
      name: pick(d.name, d.driver_name, d.driver?.name, ''),
      email: pick(d.email, d.driver?.email, ''),
      phone: pick(d.phone, d.driver?.phone, ''),
      vehicle_registration_number: pick(
        d.vehicle_registration_number,
        d.vehicleRegistrationNumber,
        d.vrn,
        d.vehicle_reg,
        d.driver?.vehicle_registration_number,
        ''
      ),
      agency_id,
      company_id,
      agency_name: pick(d.agency_name, d.agencyName, d.agency?.name, null),
      submission_date: pick(d.submission_date, d.created_at, d.submitted_at, d.driver?.submission_date, null),
      workforce_source: pick(d.workforce_source, d.workforceSource, company_id ? 'COMPANY' : 'AGENCY'),
      status: pick(d.status, d.driver?.status, ''),

      // Step 2E canonical operational fields
      assignment_id: toNum(pick(d.assignment_id, d.assignmentId)),
      assignment_status: pick(d.assignment_status, d.assignmentStatus, null),
      site_id: toNum(pick(d.site_id, d.siteId)),
      site_name: pick(d.site_name, d.siteName, null),
      current_state: pick(d.current_state, d.currentState, null),

      // ban metadata
      is_banned: pick(d.is_banned, d.isBanned, false),
      ban_id: toNum(pick(d.ban_id, d.banId)),
      ban_reason: pick(d.ban_reason, d.banReason, null),
      banned_at: pick(d.banned_at, null),
    };
  });

/**
 * Convert ban records → driver-shaped entities
 */
const normalizeBanned = (rows) =>
  normalizeDrivers(rows).map((r) => ({
    ...r,
    status: 'Banned',
    ban_id: toNum(pick(r.ban_id)),
    ban_reason: pick(r.ban_reason, r.reason, ''),
    banned_at: pick(r.banned_at, r.created_at, null),
  }));

/* ---------- FETCHING (READS) ---------- */

/**
 * Fetch UPS drivers by status.
 * ✅ CHANGED Phase 8.7B: Throws error for Active/Reserved/Confirmed.
 * Only handles 'Banned' reading now.
 */
export async function fetchDriversByStatus(status, { agencyId, search, source } = {}) {
  // Guardrail: Force devs to use assignmentService for site lists
  if (['active', 'reserved', 'confirmed', 'Active', 'Reserved'].includes(status)) {
    throw new Error(
       `[upsDriverService] fetchDriversByStatus is deprecated for '${status}'. Use fetchSiteAssignments(siteId, ...) instead.`
    );
  }

  const params = {};
  if (agencyId) params.agency_id = agencyId;
  if (search)   params.search    = search;

  const sourceParam = normalizeSourceParam(source);
  if (sourceParam) {
    params.source = sourceParam;
  }

  // We only expect 'Banned' (or 'banned') to pass through here
  // Adjust endpoint path if your backend expects lowercase 'banned'
  const safeStatus = status.toLowerCase() === 'banned' ? 'banned' : status;

  const { data } = await axios.get(`/ups/drivers/${safeStatus}`, { params });

  if (safeStatus === 'banned') {
    return normalizeBanned(data);
  }
  return normalizeDrivers(data);
}

/**
 * Fetch the UPS audit list of banned drivers.
 */
export async function fetchBannedDrivers({ agencyId, search, source } = {}) {
  return fetchDriversByStatus('banned', { agencyId, search, source });
}

/**
 * Fetch company-owned internal drivers for UPS/company admin management.
 */
export async function fetchInternalDrivers({ search } = {}) {
  const params = {};
  if (search) params.search = search;

  const { data } = await axios.get('/ups/internal-drivers', { params });
  return normalizeDrivers(data);
}

// REMOVED: fetchAllStatuses (Legacy Dashboard Read)

/* ---------- ACTIONS (WRITES) ---------- */

/** Reserve an active driver → returns a normalized driver ready for upsert */
export async function reserveDriver(id, siteKey = 'MAIN') {
  const { data } = await axios.put(`/ups/sites/${siteKey}/drivers/${id}/reserved`);
  const [normalized] = tagLegacyStatusForCompatibility([data], 'Reserved');
  return normalized ?? { id: toNum(id), driver_id: toNum(id), status: 'Reserved' };
}

/** Activate a reserved driver → normalized */
export async function activateDriver(id, siteKey = 'MAIN') {
  const { data } = await axios.put(`/ups/sites/${siteKey}/drivers/${id}/active`);
  const [normalized] = tagLegacyStatusForCompatibility([data], 'Active');
  return normalized ?? { id: toNum(id), driver_id: toNum(id), status: 'Active' };
}

/**
 * Create a company-owned internal driver.
 */
export async function createInternalDriver(payload) {
  const { data } = await axios.post('/ups/internal-drivers', payload);
  const [normalized] = normalizeDrivers([data]);
  return normalized ?? data;
}

/**
 * Update a company-owned internal driver.
 */
export async function updateInternalDriver(id, payload) {
  const { data } = await axios.put(`/ups/internal-drivers/${id}`, payload);
  const [normalized] = normalizeDrivers([data]);
  return normalized ?? data;
}

/**
 * Delete a company-owned internal driver.
 */
export async function deleteInternalDriver(id) {
  await axios.delete(`/ups/internal-drivers/${id}`);
  return { id: toNum(id), driver_id: toNum(id) };
}

/**
 * Ban a driver → server returns a full driver DTO + ban_id.
 */
export async function banDriver(driverId, banReason) {
  const payload = { driver_id: driverId, ban_reason: banReason };
  const { data } = await axios.post('/ups/ban-driver', payload);

  const [normalized] = normalizeBanned([data]);
  return normalized ?? {
    id: toNum(driverId),
    driver_id: toNum(driverId),
    status: 'Banned',
  };
}

/** Update an existing ban record's reason */
export async function updateBan(banId, banReason) {
  const { data } = await axios.put(`/ups/ban-driver/${banId}`, { ban_reason: banReason });

  const [normalized] = normalizeBanned([data]);
  return normalized ?? null;
}

/** Unban a driver */
export async function unbanDriver(banIdOrDriverId) {
  const { data } = await axios.put(`/ups/drivers/unban/${banIdOrDriverId}`);

  const [normalized] = normalizeDrivers([data]);
  return normalized ?? {
    ...(data && typeof data === 'object' ? data : {}),
    id: data?.id ?? data?.driver_id ?? toNum(banIdOrDriverId),
    driver_id: data?.id ?? data?.driver_id ?? toNum(banIdOrDriverId),
  };
}
